import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import { useTheme } from '@mui/material/styles';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { fetchParticipantEntries, type ParticipantEntry } from '../api/participantEntries';
import { computeParticipantAnalytics } from '../utils/participantAnalytics';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
  participantId: string;
}

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('ru-RU');
};

const ParticipantAnalyticsPage: React.FC<Props> = ({ participantId }) => {
  const [entries, setEntries] = useState<ParticipantEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContestId, setSelectedContestId] = useState<string>('');

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [openChart, setOpenChart] = useState<'month' | 'contest' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchParticipantEntries(participantId)
      .then((rows) => {
        if (cancelled) return;
        setEntries(rows);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message ?? 'Ошибка загрузки');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [participantId]);

  const contestOptions = useMemo(() => {
    const map = new Map<number, string>();
    for (const e of entries) {
      if (typeof e?.contest_id !== 'number') continue;
      const name = e.contest_name ?? `#${e.contest_id}`;
      map.set(e.contest_id, name);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([id, name]) => ({ id, name }));
  }, [entries]);

  const filteredEntries = useMemo(() => {
    if (!selectedContestId) return entries;
    const id = Number(selectedContestId);
    if (Number.isNaN(id)) return entries;
    return entries.filter((e) => e.contest_id === id);
  }, [entries, selectedContestId]);

  const analytics = useMemo(() => computeParticipantAnalytics(filteredEntries), [filteredEntries]);

  const chartData = useMemo(() => {
    const labels = analytics.entriesPerMonth.map((p) => p.label);
    const data = analytics.entriesPerMonth.map((p) => p.count);
    return {
      labels,
      datasets: [
        {
          label: 'Записей',
          data,
          backgroundColor: 'rgba(25, 118, 210, 0.4)',
          borderColor: 'rgba(25, 118, 210, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [analytics.entriesPerMonth]);

  const chartByContestData = useMemo(() => {
    const labels = analytics.contestBreakdown.map((c) => c.contest_name);
    const data = analytics.contestBreakdown.map((c) => c.entries);

    return {
      labels,
      datasets: [
        {
          label: 'Записей',
          data,
          backgroundColor: 'rgba(46, 125, 50, 0.35)',
          borderColor: 'rgba(46, 125, 50, 1)',
          borderWidth: 1,
        },
      ],
    };
  }, [analytics.contestBreakdown]);

  const chartOptions = useMemo(() => {
    return {
      responsive: true,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Активность по месяцам' },
      },
      scales: {
        y: { beginAtZero: true, ticks: { precision: 0 } },
      },
    } as const;
  }, []);

  const chartOptionsFullscreen = useMemo(() => {
    return {
      ...chartOptions,
      maintainAspectRatio: false,
      plugins: {
        ...(chartOptions as any).plugins,
        title: { display: true, text: 'Активность по месяцам (полный экран)' },
      },
    } as const;
  }, [chartOptions]);

  const chartByContestOptions = useMemo(() => {
    return {
      responsive: true,
      indexAxis: 'y' as const,
      plugins: {
        legend: { display: false },
        title: { display: true, text: 'Распределение по зарубам' },
      },
      scales: {
        x: { beginAtZero: true, ticks: { precision: 0 } },
      },
    } as const;
  }, []);

  const chartByContestOptionsFullscreen = useMemo(() => {
    return {
      ...chartByContestOptions,
      maintainAspectRatio: false,
      plugins: {
        ...(chartByContestOptions as any).plugins,
        title: { display: true, text: 'Распределение по зарубам (полный экран)' },
      },
      // More room for long labels
      layout: { padding: { left: 8, right: 8, top: 8, bottom: 8 } },
    } as const;
  }, [chartByContestOptions]);

  const recommendations = useMemo(() => {
     const rec: string[] = [];
     const t = analytics.trend30d;
     if (t.prev30 === 0 && t.last30 > 0) {
       rec.push('Отличный старт: за последние 30 дней появилась активность. Держи темп!');
     } else if (t.prev30 > 0) {
       if (t.delta > 0) rec.push(`Активность растёт: +${t.delta} записей за последние 30 дней по сравнению с предыдущими 30.`);
       if (t.delta < 0) rec.push(`Активность снизилась: ${t.delta} записей за последние 30 дней по сравнению с предыдущими 30. Возможно, стоит вернуть регулярность.`);
       if (t.delta === 0) rec.push('Активность стабильная последние 60 дней. Если хочешь быстрее прогрессировать — добавь 1 попытку в неделю.');
     }

    // Cross-contest recommendations should only be shown when user is NOT filtering to a single contest.
    if (!selectedContestId) {
      if (analytics.distinctContests >= 6) {
        rec.push('У тебя хорошая разносторонность. Чтобы закрепить рост, выбери 1–2 любимые зарубы и регулярно обновляй там результат.');
      }

      if (analytics.topContest && analytics.topContest.entries >= Math.max(3, Math.ceil(analytics.totalEntries * 0.5))) {
        rec.push(`Похоже, твоя основная заруба сейчас — «${analytics.topContest.contest_name}». Для универсальности попробуй добавить 1 новую зарубу в ближайший месяц.`);
      }
    }

     if (!rec.length) {
       rec.push('Продолжай в том же духе: каждый новый результат укрепляет прогресс и статистику.');
     }
     return rec;
  }, [analytics, selectedContestId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={2}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Не удалось загрузить аналитику: {error}</Alert>;
  }

  if (!entries.length) {
    return <Alert severity="info">Пока нет данных (нет заявок/результатов).</Alert>;
  }

  return (
    <Box>
      <Dialog
        open={openChart !== null}
        onClose={() => setOpenChart(null)}
        fullScreen={fullScreen}
        maxWidth="lg"
        fullWidth
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {openChart === 'month' ? 'Активность по месяцам' : openChart === 'contest' ? 'Распределение по зарубам' : ''}
          </Typography>
          <IconButton onClick={() => setOpenChart(null)} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ height: fullScreen ? 'calc(100vh - 64px)' : 520, p: 2 }}>
            {openChart === 'month' && <Bar data={chartData} options={chartOptionsFullscreen} />}
            {openChart === 'contest' && <Bar data={chartByContestData} options={chartByContestOptionsFullscreen} />}
          </Box>
        </DialogContent>
      </Dialog>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Фильтр
        </Typography>
        <Select
          value={selectedContestId}
          onChange={(e) => setSelectedContestId(String(e.target.value))}
          displayEmpty
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="">Все зарубы ({entries.length})</MenuItem>
          {contestOptions.map((c) => (
            <MenuItem key={c.id} value={String(c.id)}>
              {c.name}
            </MenuItem>
          ))}
        </Select>
        {selectedContestId && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Показана аналитика только по выбранной зарубе.
          </Typography>
        )}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Коротко
        </Typography>
        <Typography variant="body2">Всего записей: <b>{analytics.totalEntries}</b></Typography>
        <Typography variant="body2">Разных заруб: <b>{analytics.distinctContests}</b></Typography>
        <Typography variant="body2">Подиумов (1–3 место): <b>{analytics.bestPodiums}</b></Typography>
        <Typography variant="body2">Первая запись: <b>{formatDate(analytics.firstEntryAt)}</b></Typography>
        <Typography variant="body2">Последняя запись: <b>{formatDate(analytics.lastEntryAt)}</b></Typography>

        <Box mt={1}>
          <Typography variant="body2">
            Тренд активности (30 дней): <b>{analytics.trend30d.last30}</b> (пред. 30 дней: <b>{analytics.trend30d.prev30}</b>)
            {analytics.trend30d.delta !== 0 ? (
              <> — Δ <b>{analytics.trend30d.delta > 0 ? `+${analytics.trend30d.delta}` : analytics.trend30d.delta}</b></>
            ) : null}
            {analytics.trend30d.deltaPct !== null ? (
              <> ({analytics.trend30d.deltaPct >= 0 ? '+' : ''}{analytics.trend30d.deltaPct.toFixed(0)}%)</>
            ) : null}
          </Typography>
        </Box>
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Рекомендации
        </Typography>
        {recommendations.map((r, idx) => (
          <Typography key={idx} variant="body2" sx={{ lineHeight: 1.5 }}>
            • {r}
          </Typography>
        ))}
      </Paper>

      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setOpenChart('month')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') setOpenChart('month');
          }}
          sx={{ cursor: 'pointer', position: 'relative' }}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpenChart('month');
            }}
            sx={{ position: 'absolute', right: 4, top: 4, zIndex: 1 }}
            aria-label="open fullscreen"
          >
            <OpenInFullIcon fontSize="small" />
          </IconButton>
          <Bar data={chartData} options={chartOptions} />
        </Box>
      </Paper>

      {!selectedContestId ? (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Box
            role="button"
            tabIndex={0}
            onClick={() => setOpenChart('contest')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setOpenChart('contest');
            }}
            sx={{ cursor: 'pointer', position: 'relative' }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpenChart('contest');
              }}
              sx={{ position: 'absolute', right: 4, top: 4, zIndex: 1 }}
              aria-label="open fullscreen"
            >
              <OpenInFullIcon fontSize="small" />
            </IconButton>
            <Bar data={chartByContestData} options={chartByContestOptions} />
          </Box>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Распределение по зарубам скрыто, потому что выбрана одна заруба.
          </Typography>
        </Paper>
      )}

      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" color="primary" gutterBottom>
          Зарубы (все)
        </Typography>
        {selectedContestId && (
          <Typography variant="caption" color="text.secondary">
            Для выбранной зарубы таблица будет из одной строки.
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><b>Заруба</b></TableCell>
                <TableCell align="right"><b>Записей</b></TableCell>
                <TableCell align="right"><b>Лучшее место</b></TableCell>
                <TableCell><b>Последняя</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {analytics.contestBreakdown.map((r) => (
                <TableRow key={r.contest_id}>
                  <TableCell>{r.contest_name}</TableCell>
                  <TableCell align="right">{r.entries}</TableCell>
                  <TableCell align="right">{r.bestRanking ?? '—'}</TableCell>
                  <TableCell>{formatDate(r.lastDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="text.secondary">
          Экспорт CSV доступен на вкладке «Зарубы участника».
        </Typography>
      </Paper>
    </Box>
  );
};

export default ParticipantAnalyticsPage;
